import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectActiveLayerNames } from '../../store/selectors/index.js';
import { metadataOpened } from '../../store/slices/uiSlice.js';
import { layerRemoved } from '../../store/slices/layersSlice.js';
import { addLayerToMap } from '../../store/thunks/addLayerToMap.js';
import type { CatalogRecord } from '@okmaps/shared';

interface Props {
  readonly record: CatalogRecord;
}

/** A search result: title, category, an abstract snippet, and the actions. */
export default function ResultRow({ record }: Props) {
  const dispatch = useAppDispatch();
  const activeNames = useAppSelector(selectActiveLayerNames);
  const onMap = record.layerName !== undefined && activeNames.includes(record.layerName);

  return (
    <Box sx={{ px: 2, py: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {record.title}
          </Typography>

          {record.abstract && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.25,
              }}
            >
              {record.abstract}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
            <Chip label={record.category} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
            {!record.renderable && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                metadata only
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Dataset details">
            <IconButton size="small" aria-label={`Details for ${record.title}`} onClick={() => dispatch(metadataOpened(record.id))}>
              <InfoOutlined sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          {record.renderable && (
            <Tooltip title="Download">
              <IconButton
                size="small"
                aria-label={`Download ${record.title}`}
                component="a"
                href={`/api/download?typeName=${encodeURIComponent(record.layerName ?? '')}`}
              >
                <DownloadOutlined sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {record.renderable && (
        <Button
          size="small"
          fullWidth
          variant={onMap ? 'contained' : 'outlined'}
          startIcon={onMap ? <CheckIcon sx={{ fontSize: 15 }} /> : undefined}
          sx={{ mt: 1, py: 0.25, fontSize: '0.75rem' }}
          aria-pressed={onMap}
          onClick={() => {
            if (!record.layerName) return;
            if (onMap) dispatch(layerRemoved(record.layerName));
            else void dispatch(addLayerToMap({ layerName: record.layerName, zoomTo: true }));
          }}
        >
          {onMap ? 'On the map' : 'Add to map'}
        </Button>
      )}
    </Box>
  );
}
